import { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { LogOut, Menu, KeyRound, User } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { ROLE_LABELS } from "../data/constants";
import ChangePasswordModal from "./ChangePasswordModal";
import NotificationDropdown from "./NotificationDropdown";
import ProfileDetailsModal from "./ProfileDetailsModal";

export default function Header({ onMenuClick }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [drop, setDrop] = useState(false);
  const [showChangePwModal, setShowChangePwModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const h = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setDrop(false);
      }
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  const getPageTitle = () => {
    const path = location.pathname;
    if (path.startsWith("/dashboard")) return "Dashboard";
    if (path.startsWith("/products")) return "Products";
    if (path.startsWith("/users")) return "User";
    if (path.startsWith("/blogmanage")) return "Manage Blogs";
    return "Dashboard";
  };

  return (
    <div className="pt-4 px-4 pb-2 md:pr-4 md:pl-0 md:pb-4 w-full flex-shrink-0 bg-[#f0f2f5] relative z-50">
      <div 
        ref={ref}
        className="bg-white/70 backdrop-blur-[20px] rounded-[24px] h-[80px] flex items-center justify-between px-6 md:px-8 shadow-[0px_4px_32px_0px_#0000000A] border border-[#0000001A]"
      >
        {/* Page Title & Hamburger */}
        <div className="flex items-center gap-3 min-w-0">
          <button 
            onClick={onMenuClick}
            className="p-2 -ml-2 text-gray-600 hover:bg-gray-100 rounded-xl md:hidden focus:outline-none shrink-0"
            title="Open Menu"
          >
            <Menu size={22} />
          </button>
          <h1 className="text-xl md:text-2xl font-extrabold text-black tracking-tight truncate">
            {getPageTitle()}
          </h1>
        </div>

        {/* Profile & Notifications */}
        <div className="flex items-center gap-3.5">
          <NotificationDropdown />
          
          <div className="relative z-40">
            <button 
              className="flex items-center gap-3 p-1 rounded-full hover:bg-gray-50 transition-colors focus:outline-none" 
              onClick={() => setDrop((p) => !p)}
            >
              <div className="w-[42px] h-[42px] rounded-full bg-[#fca5a5] text-white flex items-center justify-center text-sm font-bold shadow-sm overflow-hidden border border-gray-100">
                 {/* Simulating the photo from target image using ui-avatars */}
                 <img src={`https://ui-avatars.com/api/?name=${encodeURIComponent(user.name)}&background=fca5a5&color=fff&bold=true&size=100`} alt="avatar" className="w-full h-full object-cover" />
              </div>
            </button>
            
            {drop && (
              <div className="absolute right-0 top-[calc(100%+8px)] min-w-[220px] bg-white rounded-xl shadow-[0_8px_30px_rgb(0,0,0,0.12)] border border-gray-100 py-2 z-[999]">
                <div className="px-4 py-3 border-b border-gray-100 bg-gray-50/50">
                  <div className="text-sm font-bold text-gray-900">{user.name}</div>
                  {/* <div className="text-xs text-gray-500 mb-1">{user.email}</div>
                  <span className="inline-block px-2 py-0.5 rounded text-[10px] font-bold bg-[#0d5f66]/10 text-[#0d5f66] uppercase tracking-wider">
                    {ROLE_LABELS[user.role] || "Admin"}
                  </span> */}
                </div>
                <div 
                  className="px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer flex items-center gap-2 mt-1 transition-colors"
                  onClick={() => {
                    setDrop(false);
                    setShowProfileModal(true);
                  }}
                >
                  <User size={16} className="text-gray-500" /> Profile Details
                </div>
                <div 
                  className="px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer flex items-center gap-2 mt-1 transition-colors"
                  onClick={() => {
                    setDrop(false);
                    setShowChangePwModal(true);
                  }}
                >
                  <KeyRound size={16} className="text-gray-500" /> Change Password
                </div>
                <div 
                  className="px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 cursor-pointer flex items-center gap-2 mt-1 transition-colors border-t border-gray-100"
                  onClick={handleLogout}
                >
                  <LogOut size={16} /> Sign Out
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <ChangePasswordModal open={showChangePwModal} onClose={() => setShowChangePwModal(false)} />
      <ProfileDetailsModal open={showProfileModal} onClose={() => setShowProfileModal(false)} />
    </div>
  );
}
